//@flow
import React from "react";
import { Nav } from "./nav";
import { Banner } from "./banner";
import List from "./list";
import PostcodeUpdate from "./postcode-update";
import { WishlistRightAside } from "./wishlist-right-aside";
import MobileActions from "./mobile-actions";

import {
  getWishlistProductIds,
  getPostCode,
  removeWishListProduct
} from "./services/storage";
import { fetchProductData } from "./services/product-data";
import { fetchPricingData } from "./services/pricing-data";
import { fetchPublicPurchasableData } from "./services/purchasability-data";
import { fetchIsUserLoggedInData } from "./services/user-isloggedin";

import type { ProductType } from "./types/product.js";
import type { PricingType } from "./types/pricing.js";
import type { ProductPurchasability } from "./types/publicpurchasable";

import { notify } from "./services/notification";

type Props = {};
type States = {
  products: Array<ProductType>,
  pricesMap: Map<number, PricingType>,
  productPurchasabilityMap: Map<number, ProductPurchasability>,
  postCodeAvailable: boolean,
  showPostcodeUpdate: boolean,
  loading: boolean,
  isLoadingPurchasability: boolean,
  isLoggedInUser: boolean
};

import "./styles/app.scss";
import "./styles/content.scss";

export class App extends React.Component<Props, States> {
  constructor(props: Props) {
    super(props);
    this.state = {
      products: [],
      pricesMap: new Map(),
      productPurchasabilityMap: new Map(),
      postCodeAvailable: false,
      showPostcodeUpdate: false,
      loading: false,
      isLoadingPurchasability: false,
      isLoggedInUser: false
    };
  }

  componentDidMount() {
    this.loadData();
  }

  async loadData() {
    try {
      const isLoggedInPromise: Promise<boolean> = fetchIsUserLoggedInData();

      const productIds = getWishlistProductIds();

      this.setState({ loading: true, isLoadingPurchasability: true });

      const productsPromise: Promise<Array<ProductType>> = fetchProductData(
        productIds
      );

      const postCode = getPostCode();

      const pricesMapPromise: Promise<Map<number, PricingType>> = postCode
        ? fetchPricingData(postCode, productIds)
        : Promise.resolve(new Map());

      const productPurchasabilityMapPromise: Promise<
        Map<number, ProductPurchasability>
      > = fetchPublicPurchasableData(productIds);

      const isLoggedInUser = await isLoggedInPromise;
      this.setState({ isLoggedInUser: isLoggedInUser });

      const products = await productsPromise;
      this.setState({ products: products, loading: false });

      const pricesMap = await pricesMapPromise;
      this.setState({ pricesMap: pricesMap, postCodeAvailable: !!postCode });

      const productPurchasabilityMap = await productPurchasabilityMapPromise;
      this.setState({
        productPurchasabilityMap: productPurchasabilityMap
      });
    } catch (e) {
      notify({ level: "ERROR", title: "Error", text: "Error getting data" });
    } finally {
      this.setState({ loading: false, isLoadingPurchasability: false });
    }
  }

  onPostcodeUpdate = async () => {
    const postCode = getPostCode();
    if (postCode) {
      this.setState({ postCodeAvailable: true, showPostcodeUpdate: false });
      const productIds = getWishlistProductIds();
      const pricesMap: Map<number, PricingType> = await fetchPricingData(
        postCode,
        productIds
      );
      this.setState({ pricesMap: pricesMap });
    }
    this.setState({ showPostcodeUpdate: false });
    ((document.body: any): HTMLElement).classList.remove("overlay-active");
  };

  onPostcodeClose = () => {
    this.setState({ showPostcodeUpdate: false });
    ((document.body: any): HTMLElement).classList.remove("overlay-active");
  };

  onShowPostcodeClicked = () => {
    this.setState({ showPostcodeUpdate: true });
    ((document.body: any): HTMLElement).classList.add("overlay-active");
  };

  onWishlistRemove = (productCode: number): void => {
    removeWishListProduct(productCode);

    this.setState(state => ({
      products: state.products.filter(
        product => product.productCode !== productCode
      )
    }));
  };

  onQuantityChanged = (productCode: number, newQuantity: number): void => {
    const newProducts = this.state.products.map(product => ({
      ...product,
      quantity:
        product.productCode === productCode ? newQuantity : product.quantity
    }));
    this.setState({ products: newProducts });
  };

  render() {
    return (
      <span>
        <Banner />
        <div className="page-body">
          <div className="wishlist-main">
            <Nav />
            <div className="wishlist-main__content">
              <List
                products={this.state.products}
                pricesMap={this.state.pricesMap}
                productPurchasabilityMap={this.state.productPurchasabilityMap}
                postCodeAvailable={this.state.postCodeAvailable}
                onRemove={this.onWishlistRemove}
                onQuantityChanged={this.onQuantityChanged}
                onShowPostcodeClicked={this.onShowPostcodeClicked}
                isLoading={this.state.loading}
                isLoadingPurchasability={this.state.isLoadingPurchasability}
                isLoggedInUser={this.state.isLoggedInUser}
              />
              {this.state.showPostcodeUpdate && (
                <PostcodeUpdate
                  onPostcodeUpdate={this.onPostcodeUpdate}
                  onPostcodeClose={this.onPostcodeClose}
                />
              )}
              <WishlistRightAside />
              <MobileActions
                products={this.state.products}
                isPostCodeAvailable={this.state.postCodeAvailable}
                productPurchasabilityMap={this.state.productPurchasabilityMap}
                isLoadingPurchasability={this.state.isLoadingPurchasability}
              />
            </div>
          </div>
        </div>
      </span>
    );
  }
}
